import React, { useState } from "react";
import SectionContainer from "../../components/SectionContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuoteLeft,
  faChevronLeft,
  faChevronRight,
  faLightbulb,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";

// CEOQuotes expects an array of objects: { text: string, author: string }
const defaultQuotes = [
  {
    text: "Focus on building products that put the user first — clarity in purpose drives better engineering decisions.",
    author: "Sundar Pichai",
    company: "Google",
    role: "CEO",
    category: "Product Focus",
    image: "/images/ceos/sundar-pichai.jpg", // placeholder
  },
  {
    text: "Sustainable growth comes from solving real problems with frugal, reliable engineering and long-term focus.",
    author: "Sridhar Vembu",
    company: "Zoho",
    role: "CEO & Co-founder",
    category: "Sustainable Growth",
    image: "/images/ceos/sridhar-vembu.jpg", // placeholder
  },
  {
    text: "A company succeeds when it fosters learning, discipline, and a strong sense of purpose across the team.",
    author: "N. R. Narayana Murthy",
    company: "Infosys",
    role: "Co-founder",
    category: "Team Excellence",
    image: "/images/ceos/narayana-murthy.jpg", // placeholder
  },
  {
    text: "Innovation distinguishes between a leader and a follower. We must continuously push boundaries.",
    author: "Tim Cook",
    company: "Apple",
    role: "CEO",
    category: "Innovation",
    image: "/images/ceos/tim-cook.jpg", // placeholder
  },
  {
    text: "The key to success is to focus our conscious mind on things we desire, not things we fear.",
    author: "Satya Nadella",
    company: "Microsoft",
    role: "CEO",
    category: "Leadership",
    image: "/images/ceos/satya-nadella.jpg", // placeholder
  },
  {
    text: "Culture is the foundation of everything we do. It's what enables extraordinary results.",
    author: "Marc Benioff",
    company: "Salesforce",
    role: "Chairman & CEO",
    category: "Culture",
    image: "/images/ceos/marc-benioff.jpg", // placeholder
  },
];

const categoryColors = {
  "Product Focus": "from-blue-500 to-indigo-600",
  "Sustainable Growth": "from-green-500 to-emerald-600",
  "Team Excellence": "from-purple-500 to-violet-600",
  Innovation: "from-orange-500 to-red-600",
  Leadership: "from-cyan-500 to-blue-500",
  Culture: "from-pink-500 to-rose-600",
};

const CEOQuotes = ({ quotes = defaultQuotes }) => {
  // Function to process quotes for backward compatibility
  const processQuotes = (inputQuotes) => {
    return inputQuotes.map((quote) => {
      // If quote already has new format, use it
      if (quote.company && quote.role && quote.category) {
        return quote;
      }

      // Convert old format to new format
      const authorParts = quote.author
        ? quote.author.split(" — ")
        : ["Unknown", "Unknown"];
      const author = authorParts[0] || "Unknown";
      const company = authorParts[1] || "Unknown";

      return {
        ...quote,
        author: author,
        company: company,
        role: "Leadership",
        category: "Leadership",
        image: "/images/ceos/default.jpg",
      };
    });
  };

  const processedQuotes = processQuotes(quotes);
  const [selectedQuote, setSelectedQuote] = useState(0);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = [
    "All",
    ...new Set(processedQuotes.map((quote) => quote.category)),
  ];
  const filteredQuotes =
    activeCategory === "All"
      ? processedQuotes
      : processedQuotes.filter((quote) => quote.category === activeCategory);

  const nextQuote = () => {
    setSelectedQuote((prev) => (prev + 1) % filteredQuotes.length);
  };

  const prevQuote = () => {
    setSelectedQuote(
      (prev) => (prev - 1 + filteredQuotes.length) % filteredQuotes.length
    );
  };

  return (
    <SectionContainer id="ceoquotes" variant="subtle">
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="quotes-pattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M15,5 Q20,10 15,15 Q10,10 15,5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#quotes-pattern)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faLightbulb} className="mr-2" />
            Leadership Wisdom
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            Leadership{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              Wisdom
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Strategic perspectives from global leaders guiding our innovation,
            growth, and commitment to excellence.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-16 relative z-10">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeCategory === category
                  ? "bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white shadow-lg"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#E5C558] hover:text-[#E5C558]"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured Quote Carousel */}
        {filteredQuotes.length > 0 && (
          <div className="mb-20 relative z-10">
            <div className="bg-gradient-to-br from-[#E5C558]/5 to-[#d4b54d]/5 rounded-3xl p-12 border border-[#E5C558]/20 backdrop-blur-sm relative">
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={prevQuote}
                  className="w-12 h-12 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center hover:bg-[#E5C558] hover:text-white transition-all duration-300 group"
                  disabled={filteredQuotes.length <= 1}
                >
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    className="group-hover:animate-pulse"
                  />
                </button>

                <div className="text-center flex-1 mx-8">
                  <FontAwesomeIcon
                    icon={faQuoteLeft}
                    className="text-6xl text-[#E5C558]/30 mb-6"
                  />
                  <blockquote className="text-2xl lg:text-3xl font-light text-gray-800 dark:text-gray-200 italic leading-relaxed mb-8">
                    "{filteredQuotes[selectedQuote]?.text}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E5C558] to-[#d4b54d] flex items-center justify-center shadow-lg">
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className="text-white text-xl"
                      />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-xl text-gray-900 dark:text-white">
                        {filteredQuotes[selectedQuote]?.author}
                      </div>
                      <div className="text-lg text-[#E5C558] font-semibold">
                        {filteredQuotes[selectedQuote]?.role}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {filteredQuotes[selectedQuote]?.company}
                      </div>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="mt-6">
                    <span
                      className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${
                        categoryColors[filteredQuotes[selectedQuote]?.category]
                      } text-white font-semibold text-sm shadow-md`}
                    >
                      {filteredQuotes[selectedQuote]?.category}
                    </span>
                  </div>
                </div>

                <button
                  onClick={nextQuote}
                  className="w-12 h-12 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center hover:bg-[#E5C558] hover:text-white transition-all duration-300 group"
                  disabled={filteredQuotes.length <= 1}
                >
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="group-hover:animate-pulse"
                  />
                </button>
              </div>

              {/* Dots Indicator */}
              {filteredQuotes.length > 1 && (
                <div className="flex justify-center space-x-2">
                  {filteredQuotes.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedQuote(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === selectedQuote
                          ? "bg-[#E5C558] scale-125"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SectionContainer>
  );
};

export default CEOQuotes;
